/// <reference path="../../typings/browser.d.ts" />

namespace Application.Controllers {
    'use strict';

    import INetworkNode = Models.INetworkNode;
    import NetworkNode = Models.NetworkNode;
    import NetworkNodeType = Models.NetworkNodeType;
    import IGateway = Models.IGateway;
    import IFlexEvent = Models.IFlexEvent;
    import GeoElement = Models.GeoElement;
    import IPaginatedList = Models.IPaginatedList;
    import INetworkInterface = Models.INetworkInterface;
    import IFilterDirectiveMap = Directives.IFilterDirectiveMap;

    /*interface IStatusFilter {
        healthStatus: string;
        selected: boolean;
    }*/

    interface IOffsettedRepo {
        totalLoaded: number;
        firstLoadFinished: boolean;
        loading: boolean;
    }

    export interface INodeWizardResponse {
        networkNode: NetworkNode;
        new: boolean;
    }

    export class NetworkNodesController {
        public static NUM_REQUESTS: number = 30;  // used for initial page size and consequent calls
        public static $name: string = "networkNodesController";
        public static $inject: string[] = ['networkNodeService',
                                           'networkNodeTypeService',
                                           'ModalService',
                                           'gatewayService',
                                           'stompService',
                                           '$scope',
                                           '$timeout',
                                           'healthService',
                                           '$location',
                                           'deviceGuiService',
                                           '$sce',
                                           'networkService',
                                           'blockService',
                                           'messageTranslationsService',
                                           '$window',
                                           'aclService',
        ];

        public networkNodes: INetworkNode[];
        public networkNodeTypes: NetworkNodeType[];
        public gateways: IGateway[];
        public addedNodes: INetworkNode[];
        public mags: NetworkNode[];

        public typeFilterMap: IFilterDirectiveMap; // for column-filter directive
        public statusFilterMap: IFilterDirectiveMap; // for column-filter directive

        private currentPage: number;
        private moreData: boolean;
        private moreDataLoaded: boolean; // tslint:disable-line:no-unused-variable // used in html
        private dataLoaded: boolean = false;

        private sortType: string;
        private sortReverse: string;
        private searchText: string;
        private searchTextForHighlight: string;

        private timeoutSearch: ng.IPromise<void>;
        private subscription: Services.IEventSubscription;

        private internet: Models.INetwork;

        private nodesRepo: IOffsettedRepo = {
            totalLoaded: 0,
            firstLoadFinished: false,
            loading: true,
        };

        /**
         * Constructor of the class.
         */
        constructor(private networkNodeService: Services.NetworkNodeService,
                    private networkNodeTypeService: Services.NetworkNodeTypeService,
                    private modalService: ngModal.IModalService,
                    private gatewayService: Services.GatewayService,
                    private stompService: Services.StompService,
                    private $scope: ng.IScope,
                    private $timeout: ng.ITimeoutService,
                    private healthService: Services.HealthService,
                    private $location: ng.ILocationService,
                    private deviceGuiService: Services.DeviceGuiService,
                    private $sce: ng.ISCEService,
                    private networkService: Services.NetworkService,
                    private blockService: Services.BlockService,
                    private messageTranslationsService: Services.MessageTranslationsService,
                    private $window: ng.IWindowService,
                    private aclService: Services.AclService) {
            
            this.initSubscriptions();
            this.initVariables();
            this.initData();

            this.$scope.$on("$destroy", () => {
                if (this.subscription)
                    this.stompService.unregister(this.subscription);
                angular.element(this.$window).unbind('scroll');
            });
        }

        public applyFilter(): void {
            this.networkNodes = [];
            this.currentPage = 0;
            this.addedNodes = [];
            this.loadNetworkNodes().then(() => {
                this.searchTextForHighlight = this.searchText;
            });
        }

        public clearFilters(): void {
            this.searchText = "";
            this.addedNodes = [];
            this.applyFilter();
        }

        public supportUrlPath(node: Models.INetworkNode): string { 
            return "/" + Common.Utils.idFromHref(node.href);
        }

        public sortColumn(type: string): void {
            this.sortType = type;
            this.sortReverse = this.sortReverse === 'ASC' ? 'DESC' : 'ASC';
            this.addedNodes = [];
            this.applyFilter();
        }

        public searchByText(): void {
            this.$timeout.cancel(this.timeoutSearch);
            this.addedNodes = [];
            this.applyFilter();
        }

        public searchAsType(): void {
            if (this.searchText !== undefined) {
                this.$timeout.cancel(this.timeoutSearch);
                this.timeoutSearch = this.$timeout(() => {
                    this.addedNodes = [];
                    this.applyFilter();
                }, 500);
            }
        }

        public launchNetworkNodeWizardModal(element?: INetworkNode): void {
            let node: INetworkNode = element ? Common.Utils.clone(element) as INetworkNode : NetworkNode.Empty();
            if (node.geoElement === undefined) node.geoElement = new GeoElement();
            if (node.geoElement.auto === undefined) node.geoElement.auto = true;
            this.modalService.showModal({
                templateUrl: Application.Controllers.NetworkNodeModalController.templateUrl,
                controller: Application.Controllers.NetworkNodeModalController.$name,
                inputs: {
                    node: node,
                    nodeTypes: this.networkNodeTypes,
                    gateways: this.gateways,
                    mags: this.mags,
                    initialStep: undefined,
                    internet: this.internet,
                    deviceGuiUrl: node.deviceGuiUrl,
                    deviceGuiUrlNoProxy: node.deviceGuiUrlNoProxy,
                },
            }).then((modal: ngModal.IModalObject) => {
                let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.NetworkNodeModalController.ID,
                                                                {bgclose: false, modal: false, keyboard: false});
                modalElem.show();
                modal.close.then((result: INodeWizardResponse) => {
                    if (result !== undefined && result.new) {
                        this.networkNodeService.getByMftSerialNumberPromise(result.networkNode.mftSerialNumber)
                            .then((networkNode: NetworkNode) => {
                                this.setPermissions([networkNode]).then((n: NetworkNode[]) => {
                                    networkNode = n[0];
                                    this.setIpAddressToShow(networkNode);
                                    this.setBlocksToShow(networkNode);
                                    this.addedNodes.unshift(networkNode);
                                });
                            });
                    }
                    modalElem.hide();
                }).catch((reason: any) => {  // tslint:disable-line:no-any
                    modalElem.hide();
                    Common.Utils.alertOnError(reason);
                });
            });
        }

        public deleteNodeModal(node: INetworkNode): void {
            let mssg: string = "This action cannot be undone.";
            if (node.type.name === "FLEX-Server") {
                this.networkNodeService.numberOfNodesWhereNodeIsAssignedAsMag(node.href).then((n: number) => {
                    if (n > 0)
                        mssg += ` <br><br><b>There ${n > 1 ? 'are' : 'is'} currently ${n} node${n > 1 ? 's' : ''} using it as MAG.</b>`;
                    this.deleteNodeModalHelper(mssg, node);
                });
            } else {
                this.deleteNodeModalHelper(mssg, node);
            }
        }

        public editPermissions(networkNode: NetworkNode): void {
            this.modalService.showModal({
                templateUrl: Application.Controllers.AclModalController.templateURL,
                controller: Application.Controllers.AclModalController.$name,
                inputs: {
                    entity: networkNode,
                    entityTypeName: 'networkNodes',
                },
            }).then((modal: ngModal.IModalObject) => {
                let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.AclModalController.ID,
                            {bgclose: false, modal: false, keyboard: false});
                modalElem.show();
                modal.close.then(() => {
                    modalElem.hide();
                });
            });
        }

        public goToMap(networkNode: NetworkNode): void {
            if (Common.Utils.exists(networkNode.x) && Common.Utils.exists(networkNode.y)) {
                this.$location.path("/network-map" + Common.Utils.getSimplifiedHrefFromString(networkNode.href));
            }
        }

        public loadMore(): ng.IPromise<void> {
            if (this.moreData && this.dataLoaded) {
                this.dataLoaded = false;
                this.addedNodes = [];
                let pageSizeToRequest: number = NetworkNodesController.NUM_REQUESTS;
                this.currentPage++;
                return this.loadNetworkNodesWithPageSize(pageSizeToRequest, this.currentPage);
            }
        }

        public maintenanceChanged(node: INetworkNode): void {
            this.networkNodeService.updateWorkflowMode(node)
                .then(() => {
                    node.workflowMode = node.isMaintenance ? "maintenance" : 'operation';
                })
                .catch((reason: ngCustom.IReason) => {
                    if (reason.status === 412) {
                        if (node.isMaintenance)
                            this.maintenanceModeErrorModal(node);
                    }
                });
        }

        public maintenanceModeErrorModal(networkNode: INetworkNode): void {
            networkNode.isMaintenance = false;
            this.modalService.showModal({
                templateUrl: Application.Controllers.InfoModalController.templateURL,
                controller: Application.Controllers.InfoModalController.$name,
                inputs: {
                    title: 'MAINTENANCE MODE',
                    description: this.messageTranslationsService.getMessageAsStringByKeySync("networknodes.setmaintenance.transmitting"),
                },
            }).then((modal: ngModal.IModalObject) => {
                let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.InfoModalController.ID,
                    {bgclose: false, modal: false, keyboard: false});
                modalElem.show();
                modal.close.then((result: Object) => {
                    modalElem.hide();
                });
            });
        }
        
        public showHistoric(node: Models.NetworkNode): boolean {
            let typeIdAsString: string = "" + node.type.id;
            return typeIdAsString === "1" || typeIdAsString === "2" ||
                   typeIdAsString === "7" || typeIdAsString === "3" ||
                   typeIdAsString === "11" || typeIdAsString === "8";
        }

        public isMvpDevice(node: Models.NetworkNode): boolean {
            return node.type.vendor === 'MVP';
        }

        public deviceHasBitrate(node: Models.NetworkNode): boolean {
            return node.type.vendor !== 'NEVION';
        }


        public showDeviceWebInterface(node: Models.NetworkNode): boolean {
            return node.type.name !== 'IKUSNET-QUAD';
        }


        public openAdminSettings(node: INetworkNode ): void {
            if (node.deviceGuiUrl === undefined || node.deviceGuiUrl === '') {
                this.modalService.showModal({
                    templateUrl: Application.Controllers.InfoModalController.templateURL,
                    controller: Application.Controllers.InfoModalController.$name,
                    inputs: {
                        title: 'DEVICE WEB INTERFACE',
                        description: this.messageTranslationsService
                                         .getMessageAsStringByKeySync("error.networknodes.openadmin.notavailable"),
                    },
                }).then((modal: ngModal.IModalObject) => {
                    let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.InfoModalController.ID,
                        {bgclose: false, modal: false, keyboard: false});
                    modalElem.show();
                    modal.close.then((result: Object) => {
                        modalElem.hide();
                    });
                });
            } else {
                window.open(node.deviceGuiUrl.toString(), '_blank');
            }
        }

        public hasHighlightedBlocks(blocks: Models.IBlock[]): boolean {
            if (blocks === undefined || blocks.length === 0 ||
                this.searchTextForHighlight === undefined || this.searchTextForHighlight === "") {
                return false;
            }

            let regExp: RegExp = new RegExp(this.searchTextForHighlight, "ig");
            for (let block of blocks) {
                if (regExp.test(block.name) !== null) // tslint:disable-line
                    return true;
            }
            return false;
        }

        private deleteNodeModalHelper(mssg: string, node: INetworkNode): void {
            this.modalService.showModal({
                templateUrl: Application.Controllers.ConfirmModalController.templateURL,
                controller: Application.Controllers.ConfirmModalController.$name,
                inputs: {
                    title: 'DELETE NODE',
                    description: mssg,
                    name: node.name,
                    type: 'warning',
                },
            }).then((modal: ngModal.IModalObject) => {
                let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.ConfirmModalController.ID,
                    {bgclose: false, modal: false, keyboard: false});
                modalElem.show();
                modal.close.then((result: Object) => {
                    modalElem.hide();
                    if (result) {
                        $('#saving-spinner').css({display: 'block'});
                        this.networkNodeService.deleteNetworkNode(node).then(() => {
                                $('#saving-spinner').css({display: 'none'});
                        });
                    }
                });
            });
        }

        private setIpAddressToShow(networkNode: INetworkNode): void {
            if (networkNode.networkInterfaces.length === 1) {
                networkNode.ipAddressToShow = networkNode.networkInterfaces[0].dhcp ? 'DHCP' : networkNode.networkInterfaces[0].ipAddress;
            } else if (networkNode.networkInterfaces.length > 1) {
                networkNode.networkInterfaces.sort((a: INetworkInterface, b: INetworkInterface) => {
                    return (a.id > b.id) ? 1 : -1;
                });
                for (let nic of networkNode.networkInterfaces) {
                    if (nic.ipAddress && nic.ipAddress !== '' && nic.ipAddress !== '0.0.0.0' && !nic.dhcp) {
                        networkNode.ipAddressToShow = nic.ipAddress;
                    }
                }
                if (!networkNode.ipAddressToShow) {
                    networkNode.ipAddressToShow = networkNode.networkInterfaces[0].dhcp ? 
                        'DHCP' : networkNode.networkInterfaces[0].ipAddress;
                }
            }
        }

        private setBlocksToShow(networkNode: NetworkNode): void {
                this.blockService.blocksInNode(Common.Utils.idFromHref(networkNode.href)).then(
                    (blocks: Models.IBlock[]) => {
                        networkNode.blocks = blocks.sort((a: Models.IBlock, b: Models.IBlock) => a.id - b.id);
                    });
        }

        private getFilteredStatus(): string[] {
            return Object.keys(this.statusFilterMap).filter((status: string) => {
                return this.statusFilterMap[status] === true;
            }).map((status: string) => {
                return status;
            });
        }   

        private getFilteredTypes(): number[] {
            let result: number[] = [];
            this.networkNodeTypes.forEach((nodeType: NetworkNodeType) => {
                if (this.typeFilterMap[nodeType.name])
                    result.push(Common.Utils.idFromHref(nodeType.href));
            });
            return result;
        }
        
        private loadNetworkNodes(): ng.IPromise<void> {
            this.dataLoaded = false;
            let pageSizeToRequest: number = NetworkNodesController.NUM_REQUESTS;
            return this.loadNetworkNodesWithPageSize(pageSizeToRequest, this.currentPage);
        }

        private loadNetworkNodesWithPageSize(pageSizeToRequest: number, pageNumber: number): ng.IPromise<void> {
            this.nodesRepo.loading = true;
            return this.networkNodeService.getEnabledFilteredNetworkNodes(pageNumber, pageSizeToRequest,
                this.sortType, this.sortReverse, this.searchText, this.getFilteredTypes(), [], this.getFilteredStatus())
                .then((networkNodes: IPaginatedList<NetworkNode>) => {

                    // Preventive measure to avoid displaying nodes with no type (nodes created before implementing the 'visible' field)
                    networkNodes.elements = networkNodes.elements.filter((node: INetworkNode) => {
                        return node.type.name !== undefined && node.type.name !== '';
                    });

                    this.setPermissions(networkNodes.elements).then((nn: NetworkNode[]) => {
                        networkNodes.elements = nn;

                        for (let n of networkNodes.elements) {
                            this.setBlocksToShow(n);

                            let id: number = Common.Utils.idFromHref(n.href);
                            if (n.evaluatedPermissions.gui) {
                                this.deviceGuiService.getDeviceGuiByIdPromise(id).then((deviceGuiAux: Models.DeviceGui) => {
                                    if (deviceGuiAux.guiUrl !== undefined && deviceGuiAux.guiUrl !== '' && 
                                        deviceGuiAux.proxyType !== "HOST_DISABLED") {
                                        (n as INetworkNode).deviceGuiUrl = this.$sce.trustAsResourceUrl(deviceGuiAux.guiUrl);
                                        (n as INetworkNode).deviceGuiUrlNoProxy = this.$sce.trustAsResourceUrl(deviceGuiAux.guiUrlNoProxy);
                                    }
                                });
                            }

                            this.setIpAddressToShow(n);
                        }

                        if (!this.networkNodes || this.networkNodes.length === 0) {
                            this.networkNodes = networkNodes.elements;
                        } else {
                            for (let n of networkNodes.elements) {
                                let found: INetworkNode[] = this.networkNodes.filter((nnn: INetworkNode) => {
                                    return n.href === nnn.href;
                                });
                                if (found.length === 0) {
                                    this.networkNodes.push(n);
                                }
                            }
                        }

                        if (networkNodes.page.totalPages > (pageNumber + 1)) {
                            this.moreData = true;
                        } else {
                            this.moreData = false;
                        }

                        this.dataLoaded = true;
                        this.nodesRepo.loading = false;
                        this.nodesRepo.totalLoaded = this.networkNodes ? this.networkNodes.length : 0;
                    });
                });
        }

        private setPermissions(networkNodes: NetworkNode[]): ng.IPromise<NetworkNode[]> {
            let ids: number[] = networkNodes.map((n: INetworkNode) => {
                return Common.Utils.idFromHref(n.href);
            });

            return this.aclService.getPermissionsList('networkNodes', ids).then((evaluatedAcls: Models.IEvaluatedAcl[]) => {
                if (!evaluatedAcls || evaluatedAcls.length !== networkNodes.length) {
                    for (let i: number = 0; i < networkNodes.length; i++) {
                        networkNodes[i].canWrite = false;
                    }
                } else {
                    for (let i: number = 0; i < networkNodes.length; i++) {
                        networkNodes[i].evaluatedPermissions = evaluatedAcls[i];
                        networkNodes[i].canWrite = evaluatedAcls[i].write;
                    }
                }
                return networkNodes;

            }).catch(() => {
                for (let i: number = 0; i < networkNodes.length; i++) {
                    networkNodes[i].canWrite = false;
                }
                return networkNodes;
            });
        }

        private loadNodeTypes(): ng.IPromise<void> {
            return this.networkNodeTypeService.getAllNetworkNodeTypes().then((networkNodesTypes: NetworkNodeType[]) => {
                this.networkNodeTypes = networkNodesTypes;
            });
        }

        private loadGateways(): void {
            this.gatewayService.getAllGatewaysPromise().then((gateways: IGateway[]) => {
                this.gateways = gateways;
            });
        }

        private updateNetworkNode(flexEvent: IFlexEvent, href: string): INetworkNode {
            let modifiedNetworkNode: INetworkNode = flexEvent.data;
            modifiedNetworkNode.href = href;
            this.networkNodeTypeService.findById(Number(modifiedNetworkNode.typeId))
                .then((type: NetworkNodeType) => {
                    modifiedNetworkNode.type = type;
                });
            this.setIpAddressToShow(modifiedNetworkNode);
            this.setBlocksToShow( modifiedNetworkNode as NetworkNode );
            modifiedNetworkNode.health = this.healthService.getNodeHealth(modifiedNetworkNode.href);
            modifiedNetworkNode.isMaintenance = modifiedNetworkNode.workflowMode === 'maintenance';
            return modifiedNetworkNode;
        }

        private updateAddedNodes(flexEvent: IFlexEvent, id: number): void {
            this.addedNodes.forEach((addedNode: INetworkNode, index: number) => {
                if (id === Common.Utils.idFromHref(addedNode.href)) {
                    this.addedNodes[index] = this.updateNetworkNode(flexEvent, addedNode.href);
                }
            });
        }

        private loadMags(): void {
            this.networkNodeService.getAllByNetworkNodeTypeId(Models.MAG_NETWORK_NODE_TYPE).then((mags: Models.NetworkNode[]) => {
                this.mags = mags;
            });
        }

        private loadFilterTypeMap(): void {
            this.typeFilterMap = {};
            this.networkNodeTypes.forEach((nodeType: NetworkNodeType) => {
                this.typeFilterMap[nodeType.name] = false;
            });
        }

        private loadStatusFilterMap(): void {
            this.statusFilterMap = {};
            let online: string = "ONLINE";
            let offline: string = "OFFLINE";
            this.statusFilterMap[online] = false;
            this.statusFilterMap[offline] = false;
        }

        /**
         * Initiliaze methods
         */
        private initData(): void {
            this.loadGateways();
            this.loadMags();
            this.loadStatusFilterMap();
            this.loadNodeTypes().then(() => {
                this.loadFilterTypeMap();
                // this.loadNetworkNodes();
                this.dataLoaded = false;
                this.loadNetworkNodesWithPageSize(NetworkNodesController.NUM_REQUESTS, this.currentPage).then(() => {
                    this.nodesRepo.firstLoadFinished = true;
                    this.launchScrollEvent();
                });
            });
        }

        private initVariables(): void {
            this.searchText = "";
            this.sortType = NetworkNode.getDefaultOrderColumn();
            this.sortReverse = 'ASC';
            this.moreDataLoaded = true;
            this.currentPage = 0;
            this.addedNodes = [];
            this.networkService.getNetworkById(1).then((data: Models.INetwork) => {
                this.internet = data;
            });
        }

        private initSubscriptions(): void {
            this.subscription = this.stompService.register(Models.NETWORK_NODES_COMPONENT, Models.ALL_OPERATIONS,
                                                        (flexEvent: IFlexEvent) => {
                this.messageReceived(flexEvent);
            });
        }

        private messageReceived(flexEvent: IFlexEvent): void {
            let nodeId: number = flexEvent.data.id;
            if (flexEvent.operation === Models.DELETE_NETWORK_NODE_OPERATION) {
                this.networkNodes.forEach((node: INetworkNode, index: number) => {
                    if (Common.Utils.idFromHref(node.href) === nodeId) {
                        this.networkNodes.splice(index, 1);
                    }
                });
                this.addedNodes.forEach((node: INetworkNode, index: number) => {
                    if (Common.Utils.idFromHref(node.href) === nodeId) {
                        this.addedNodes.splice(index, 1);
                    }
                });
            } else if (flexEvent.operation === Models.SAVE_NETWORK_NODE_OPERATION) {
                
                if (this.addedNodes.length > 0) {
                    this.updateAddedNodes(flexEvent, nodeId);
                } else {
                    this.networkNodes.forEach((node: INetworkNode, index: number) => {
                        if (Common.Utils.idFromHref(node.href) === nodeId) {
                            let canWrite: boolean = this.networkNodes[index].canWrite;
                            this.networkNodes[index] = this.updateNetworkNode(flexEvent, node.href);
                            this.networkNodes[index].canWrite = canWrite;
                        }
                    });
                }
            } else if (flexEvent.operation === Models.WORKFLOW_CHANGED_OPERATION) {
                let workflowModeKey: string = "workflowMode";
                if (flexEvent.data[workflowModeKey] !== Models.WORKFLOW_MODE_MAINTENANCE) {
                    this.loadNetworkNodesWithPageSize(NetworkNodesController.NUM_REQUESTS, this.currentPage);
                }
            }
            this.loadMags();
        }

        private launchScrollEvent(): void {
            angular.element(this.$window).bind('scroll', () => {
                if (this.moreData && !this.nodesRepo.loading &&
                    (this.$window.innerHeight + this.$window.scrollY) >= document.body.scrollHeight) {
                        this.loadMore();
                }
            });
        }
    }
}
